/**
 * Every Brain endpoint, grouped the way the packages behind them are.
 *
 * Thin on purpose: the shared `api` client already unwraps the envelope and
 * throws a typed error, so these functions exist to name the routes once.
 */

import { api } from '@/core/api/base.api.client'
import type {
  Agent,
  ApplyResult,
  AvailableConnection,
  BrainSource,
  DqConditionCatalogue,
  DqOperator,
  DqReport,
  CypherEngine,
  CypherResult,
  ExplorerLimits,
  ExplorerStats,
  DqRule,
  DraftResult,
  EgoGraph,
  EdgeTypeDef,
  ImportResult,
  ImportScopeOption,
  Graph,
  GraphModel,
  GraphStats,
  Lineage,
  MatchMethodSpec,
  MatchRule,
  MatchTestResult,
  MergeAuditRow,
  ModelProposal,
  NodeTypeDef,
  NodeTypeDetail,
  LinkReport,
  ObjectRows,
  ObjectTable,
  PipelineRun,
  PipelineStatus,
  PropertyDef,
  ProviderStatus,
  QueryLibrary,
  RecordCounts,
  RecordRelationships,
  RecordRow,
  RelationshipPage,
  RelationshipSummary,
  ReviewPair,
  SearchHit,
  ObjectPreview,
  SavedQuery,
  SourceConnector,
  ConnectionObjects,
  StagedRecordRow,
  StrategySpec,
  SurvivorshipRule,
  SurvivorshipTestResult,
} from '../types/brain.types'

const base = (graphId: string) => `/brain/graphs/${graphId}`

interface Paged<T> {
  records: T[]
  meta: { page: number; per_page: number; total: number }
}

export const graphsApi = {
  list: () => api.get<{ graphs: Graph[] }>('/brain/graphs').then((d) => d.graphs),
  create: (body: { name: string; description?: string; requirement?: string }) =>
    api.post<{ graph: Graph }>('/brain/graphs', body).then((d) => d.graph),
  get: (graphId: string) =>
    api.get<{ graph: Graph }>(base(graphId)).then((d) => d.graph),
  update: (graphId: string, body: Partial<Graph>) =>
    api.patch<{ graph: Graph }>(base(graphId), body).then((d) => d.graph),
  remove: (graphId: string) => api.delete<unknown>(base(graphId)),
  model: (graphId: string) => api.get<GraphModel>(`${base(graphId)}/model`),
  stats: (graphId: string) => api.get<GraphStats>(`${base(graphId)}/stats`),
  publish: (graphId: string, body: { version: string; note?: string }) =>
    api.post<{ version: string; published_at: string }>(`${base(graphId)}/publish`, body),
  versions: (graphId: string) =>
    api
      .get<{ versions: { version: string; note: string | null; published_by: string | null; at: string }[] }>(
        `${base(graphId)}/versions`,
      )
      .then((d) => d.versions),
}

export const nodeTypesApi = {
  list: (graphId: string) =>
    api.get<{ node_types: NodeTypeDef[] }>(`${base(graphId)}/node-types`).then((d) => d.node_types),
  detail: (graphId: string, nodeTypeId: string) =>
    api.get<NodeTypeDetail>(`${base(graphId)}/node-types/${nodeTypeId}`),
  create: (graphId: string, body: Record<string, unknown>) =>
    api.post<{ node_type: NodeTypeDef }>(`${base(graphId)}/node-types`, body).then((d) => d.node_type),
  update: (graphId: string, nodeTypeId: string, body: Record<string, unknown>) =>
    api
      .patch<{ node_type: NodeTypeDef }>(`${base(graphId)}/node-types/${nodeTypeId}`, body)
      .then((d) => d.node_type),
  remove: (graphId: string, nodeTypeId: string) =>
    api.delete<unknown>(`${base(graphId)}/node-types/${nodeTypeId}`),
  move: (graphId: string, nodeTypeId: string, x: number, y: number) =>
    api.post<unknown>(`${base(graphId)}/node-types/${nodeTypeId}/position`, { x, y }),

  createProperty: (graphId: string, nodeTypeId: string, body: Record<string, unknown>) =>
    api
      .post<{ property: PropertyDef }>(`${base(graphId)}/node-types/${nodeTypeId}/properties`, body)
      .then((d) => d.property),
  updateProperty: (
    graphId: string,
    nodeTypeId: string,
    propertyId: string,
    body: Record<string, unknown>,
  ) =>
    api
      .patch<{ property: PropertyDef }>(
        `${base(graphId)}/node-types/${nodeTypeId}/properties/${propertyId}`,
        body,
      )
      .then((d) => d.property),
  removeProperty: (graphId: string, nodeTypeId: string, propertyId: string) =>
    api.delete<unknown>(`${base(graphId)}/node-types/${nodeTypeId}/properties/${propertyId}`),
}

export const edgeTypesApi = {
  list: (graphId: string) =>
    api.get<{ edge_types: EdgeTypeDef[] }>(`${base(graphId)}/edge-types`).then((d) => d.edge_types),
  create: (graphId: string, body: Record<string, unknown>) =>
    api.post<{ edge_type: EdgeTypeDef }>(`${base(graphId)}/edge-types`, body).then((d) => d.edge_type),
  update: (graphId: string, edgeTypeId: string, body: Record<string, unknown>) =>
    api
      .patch<{ edge_type: EdgeTypeDef }>(`${base(graphId)}/edge-types/${edgeTypeId}`, body)
      .then((d) => d.edge_type),
  remove: (graphId: string, edgeTypeId: string) =>
    api.delete<unknown>(`${base(graphId)}/edge-types/${edgeTypeId}`),
}

export const dqApi = {
  operators: (graphId: string) =>
    api.get<{ operators: DqOperator[] }>(`${base(graphId)}/dq/operators`).then((d) => d.operators),
  conditions: (graphId: string) =>
    api.get<DqConditionCatalogue>(`${base(graphId)}/dq/conditions`),
  list: (graphId: string, nodeTypeId?: string) =>
    api
      .get<{ rules: DqRule[] }>(`${base(graphId)}/dq/rules`, { node_type_id: nodeTypeId })
      .then((d) => d.rules),
  create: (graphId: string, nodeTypeId: string, body: Record<string, unknown>) =>
    api
      .post<{ rule: DqRule }>(
        `${base(graphId)}/dq/rules?node_type_id=${encodeURIComponent(nodeTypeId)}`,
        body,
      )
      .then((d) => d.rule),
  update: (graphId: string, ruleId: string, body: Record<string, unknown>) =>
    api.patch<{ rule: DqRule }>(`${base(graphId)}/dq/rules/${ruleId}`, body).then((d) => d.rule),
  remove: (graphId: string, ruleId: string) =>
    api.delete<unknown>(`${base(graphId)}/dq/rules/${ruleId}`),
  reorder: (graphId: string, nodeTypeId: string, orderedIds: string[]) =>
    api
      .patch<{ rules: DqRule[] }>(
        `${base(graphId)}/dq/rules/reorder?node_type_id=${encodeURIComponent(nodeTypeId)}`,
        { ordered_ids: orderedIds },
      )
      .then((d) => d.rules),
  test: (graphId: string, nodeTypeId: string, record: Record<string, unknown>) =>
    api.post<DqReport>(
      `${base(graphId)}/dq/test?node_type_id=${encodeURIComponent(nodeTypeId)}`,
      { record },
    ),
  compliance: (graphId: string) =>
    api
      .get<{
        rules: {
          rule_id: string
          rule_key: string
          rule_name: string
          node_type_id: string
          severity: string
          checked: number
          passed: number
          pass_rate: number | null
        }[]
      }>(`${base(graphId)}/dq/compliance`)
      .then((d) => d.rules),
}

export const matchingApi = {
  methods: (graphId: string) =>
    api.get<{ methods: MatchMethodSpec[] }>(`${base(graphId)}/matching/methods`).then((d) => d.methods),
  list: (graphId: string, nodeTypeId?: string) =>
    api
      .get<{ rules: MatchRule[] }>(`${base(graphId)}/matching/rules`, { node_type_id: nodeTypeId })
      .then((d) => d.rules),
  create: (graphId: string, nodeTypeId: string, body: Record<string, unknown>) =>
    api
      .post<{ rule: MatchRule }>(
        `${base(graphId)}/matching/rules?node_type_id=${encodeURIComponent(nodeTypeId)}`,
        body,
      )
      .then((d) => d.rule),
  update: (graphId: string, ruleId: string, body: Record<string, unknown>) =>
    api
      .patch<{ rule: MatchRule }>(`${base(graphId)}/matching/rules/${ruleId}`, body)
      .then((d) => d.rule),
  remove: (graphId: string, ruleId: string) =>
    api.delete<unknown>(`${base(graphId)}/matching/rules/${ruleId}`),
  reorder: (graphId: string, nodeTypeId: string, orderedIds: string[]) =>
    api
      .patch<{ rules: MatchRule[] }>(
        `${base(graphId)}/matching/rules/reorder?node_type_id=${encodeURIComponent(nodeTypeId)}`,
        { ordered_ids: orderedIds },
      )
      .then((d) => d.rules),
  test: (
    graphId: string,
    nodeTypeId: string,
    left: Record<string, unknown>,
    right: Record<string, unknown>,
  ) =>
    api
      .post<{ results: MatchTestResult[] }>(
        `${base(graphId)}/matching/test?node_type_id=${encodeURIComponent(nodeTypeId)}`,
        { left, right },
      )
      .then((d) => d.results),
  reviews: (graphId: string, status = 'PENDING', nodeTypeId?: string) =>
    api
      .get<{ reviews: ReviewPair[] }>(`${base(graphId)}/matching/reviews`, {
        status,
        ...(nodeTypeId ? { node_type_id: nodeTypeId } : {}),
      })
      .then((d) => d.reviews),
  resolve: (graphId: string, reviewId: string, action: 'merge' | 'reject', note?: string) =>
    api.post<unknown>(`${base(graphId)}/matching/reviews/${reviewId}/resolve`, { action, note }),
  /** Score the pair again, against the records as they are now. */
  resubmit: (graphId: string, reviewId: string) =>
    api.post<{ outcome: 'merged' | 'review' | 'distinct'; score: number; review_id: string }>(
      `${base(graphId)}/matching/reviews/${reviewId}/resubmit`,
      {},
    ),
}

export const survivorshipApi = {
  strategies: (graphId: string) =>
    api
      .get<{ strategies: StrategySpec[] }>(`${base(graphId)}/survivorship/strategies`)
      .then((d) => d.strategies),
  list: (graphId: string, nodeTypeId?: string) =>
    api
      .get<{ rules: SurvivorshipRule[] }>(`${base(graphId)}/survivorship/rules`, {
        node_type_id: nodeTypeId,
      })
      .then((d) => d.rules),
  create: (graphId: string, nodeTypeId: string, body: Record<string, unknown>) =>
    api
      .post<{ rule: SurvivorshipRule }>(
        `${base(graphId)}/survivorship/rules?node_type_id=${encodeURIComponent(nodeTypeId)}`,
        body,
      )
      .then((d) => d.rule),
  update: (graphId: string, ruleId: string, body: Record<string, unknown>) =>
    api
      .patch<{ rule: SurvivorshipRule }>(`${base(graphId)}/survivorship/rules/${ruleId}`, body)
      .then((d) => d.rule),
  remove: (graphId: string, ruleId: string) =>
    api.delete<unknown>(`${base(graphId)}/survivorship/rules/${ruleId}`),
  test: (graphId: string, nodeTypeId: string, candidates: Record<string, unknown>[]) =>
    api.post<SurvivorshipTestResult>(
      `${base(graphId)}/survivorship/test?node_type_id=${encodeURIComponent(nodeTypeId)}`,
      { candidates },
    ),
  audit: (graphId: string, entityId?: string) =>
    api
      .get<{ audit: MergeAuditRow[] }>(`${base(graphId)}/survivorship/audit`, {
        entity_id: entityId,
      })
      .then((d) => d.audit),
}

export const recordsApi = {
  list: (
    graphId: string,
    params: { node_type_key?: string; status?: string; q?: string; page?: number; per_page?: number },
  ) => api.get<Paged<RecordRow>>(`${base(graphId)}/records`, params),
  /** Every panel's count in one request — staged, rejected, matches, golden. */
  counts: (graphId: string) => api.get<RecordCounts>(`${base(graphId)}/records/counts`),
  staged: (graphId: string, params: { status?: string; node_type_key?: string; page?: number }) =>
    api.get<Paged<StagedRecordRow>>(`${base(graphId)}/records/staged`, params),
  resubmit: (graphId: string, recordId: string) =>
    api.post<{ record_id: string; status: string }>(
      `${base(graphId)}/records/staged/${recordId}/resubmit`,
      {},
    ),
  editStaged: (graphId: string, recordId: string, properties: Record<string, unknown>) =>
    api.patch<{ record: StagedRecordRow }>(`${base(graphId)}/records/staged/${recordId}`, {
      properties,
    }),
  get: (graphId: string, entityId: string) =>
    api.get<{ record: RecordRow }>(`${base(graphId)}/records/${entityId}`).then((d) => d.record),
  create: (graphId: string, body: { node_type_key: string; properties: Record<string, unknown> }) =>
    api.post<{ record: RecordRow }>(`${base(graphId)}/records`, body).then((d) => d.record),
  update: (graphId: string, entityId: string, body: Record<string, unknown>) =>
    api.patch<{ record: RecordRow }>(`${base(graphId)}/records/${entityId}`, body).then((d) => d.record),
  /** Re-resolve every declared link across the records already in the graph. */
  rebuildRelationships: (graphId: string) =>
    api.post<{
      records: number
      resolved: number
      edges: number
      new_edges: number
      relabelled: number
    }>(
      `${base(graphId)}/records/rebuild-relationships`,
      {},
    ),
  ego: (graphId: string, entityId: string, depth = 1) =>
    api.get<EgoGraph>(`${base(graphId)}/records/${entityId}/ego`, { depth }),
  lineage: (graphId: string, entityId: string) =>
    api.get<Lineage>(`${base(graphId)}/records/${entityId}/lineage`),
}

export const objectsApi = {
  tables: (graphId: string) =>
    api.get<{ tables: ObjectTable[] }>(`${base(graphId)}/objects`).then((d) => d.tables),
  rows: (
    graphId: string,
    nodeTypeId: string,
    params: { q?: string; object_id?: string; entity_id?: string; page?: number; per_page?: number },
  ) => api.get<ObjectRows>(`${base(graphId)}/objects/${nodeTypeId}`, params),
  row: (graphId: string, nodeTypeId: string, objectId: string) =>
    api.get<{ table: ObjectTable; row: Record<string, unknown> }>(
      `${base(graphId)}/objects/${nodeTypeId}/rows/${objectId}`,
    ),
  rebuild: (graphId: string) =>
    api.post<{ tables: number; rows: number }>(`${base(graphId)}/objects/rebuild`),
}

export const sourcesApi = {
  /** Step one: connectors, each with the connections you already have for it. */
  connectors: (graphId: string) =>
    api
      .get<{ connectors: SourceConnector[] }>(`${base(graphId)}/sources/connectors`)
      .then((d) => d.connectors),
  /** Step three: what one connection can be asked for. */
  objects: (graphId: string, connectionId: string) =>
    api.get<ConnectionObjects>(`${base(graphId)}/sources/objects`, {
      connection_id: connectionId,
    }),
  /** Step four's evidence: a few real rows from the chosen object. */
  preview: (
    graphId: string,
    body: { connection_id: string; action_id: string; input?: Record<string, unknown> },
  ) => api.post<ObjectPreview>(`${base(graphId)}/sources/preview`, body),
  list: (graphId: string) =>
    api.get<{ sources: BrainSource[]; available: AvailableConnection[] }>(`${base(graphId)}/sources`),
  attach: (graphId: string, body: Record<string, unknown>) =>
    api.post<{ source: BrainSource }>(`${base(graphId)}/sources`, body).then((d) => d.source),
  update: (graphId: string, sourceRowId: string, body: Record<string, unknown>) =>
    api
      .patch<{ source: BrainSource }>(`${base(graphId)}/sources/${sourceRowId}`, body)
      .then((d) => d.source),
  detach: (graphId: string, sourceRowId: string) =>
    api.delete<unknown>(`${base(graphId)}/sources/${sourceRowId}`),
  ingest: (
    graphId: string,
    sourceRowId: string,
    body: { records: Record<string, unknown>[]; id_field?: string; entity_hint?: string },
  ) =>
    api.post<{ received: number; staged: number; duplicates: number }>(
      `${base(graphId)}/sources/${sourceRowId}/ingest`,
      body,
    ),

  /** What this source can be asked to import, in the user's words. */
  importScopes: (graphId: string, sourceRowId: string) =>
    api.get<{
      connector_id: string
      connector_name: string
      scopes: ImportScopeOption[]
    }>(`${base(graphId)}/sources/${sourceRowId}/import-scopes`),

  /** Fetch from the connected app and land the rows in staging. */
  runImport: (
    graphId: string,
    sourceRowId: string,
    body: { action_id: string; input?: Record<string, unknown>; entity_hint?: string },
  ) =>
    api.post<ImportResult>(`${base(graphId)}/sources/${sourceRowId}/import`, body),
  /** Publish the whole graph: every source it reads from, then the rules. */
  publishAll: (graphId: string) =>
    api.post<{
      sources: { source_id: string; ok: boolean; error?: string; staged?: number }[]
      published: number
      staged: number
      failed: { source_id: string; error?: string }[]
      run_id: string
      counts: Record<string, Record<string, number>>
    }>(`${base(graphId)}/sources/publish`, {}),
  /** Publish one source: everything it reads, then the rules. */
  publish: (graphId: string, sourceRowId: string) =>
    api.post<{
      objects: {
        source_id: string
        ok: boolean
        error?: string
        staged?: number
        pages?: number
        sync?: { forward_rows: number; backfill_rows: number; backfill_done: boolean; more: boolean }
      }[]
      published: number
      staged: number
      failed: { source_id: string; error?: string }[]
      run_id: string
      counts: Record<string, Record<string, number>>
    }>(`${base(graphId)}/sources/${sourceRowId}/publish`, {}),
  /** Forget where the sync got to; the next pass starts from the top. */
  resetSync: (graphId: string, sourceRowId: string) =>
    api.post<{ source: BrainSource }>(`${base(graphId)}/sources/${sourceRowId}/sync/reset`, {}),
}

export const agentsApi = {
  list: (graphId: string) =>
    api.get<{ agents: Agent[]; providers: ProviderStatus }>(`${base(graphId)}/agents`),
  create: (graphId: string, body: Record<string, unknown>) =>
    api.post<{ agent: Agent }>(`${base(graphId)}/agents`, body).then((d) => d.agent),
  update: (graphId: string, agentId: string, body: Record<string, unknown>) =>
    api.patch<{ agent: Agent }>(`${base(graphId)}/agents/${agentId}`, body).then((d) => d.agent),
  remove: (graphId: string, agentId: string) =>
    api.delete<unknown>(`${base(graphId)}/agents/${agentId}`),
}

/**
 * Relationships between *records*.
 *
 * The model's edge types say what may exist; these say what does — which is the
 * question when a link was declared and nothing appears to have happened.
 */
export const relationshipsApi = {
  list: (
    graphId: string,
    params: {
      edge_type_key?: string
      entity_id?: string
      node_type_key?: string
      q?: string
      page?: number
      per_page?: number
    },
  ) => api.get<RelationshipPage>(`${base(graphId)}/relationships`, params),
  summary: (graphId: string) =>
    api.get<RelationshipSummary>(`${base(graphId)}/relationships/summary`),
  /** Per declared link: how many records carry a key, and how many resolved. */
  links: (graphId: string) => api.get<LinkReport>(`${base(graphId)}/relationships/links`),
  of: (graphId: string, entityId: string) =>
    api.get<RecordRelationships>(`${base(graphId)}/relationships/of/${entityId}`),
}

/**
 * The Graph Explorer.
 *
 * `cypher` resolves rather than throws when a query is refused or fails — a
 * rejected query is an answer the console has to render, not a request error.
 */
export const explorerApi = {
  stats: (graphId: string, refresh = false) =>
    api.get<ExplorerStats>(`${base(graphId)}/explorer/stats`, { refresh }),
  limits: (graphId: string) => api.get<ExplorerLimits>(`${base(graphId)}/explorer/limits`),
  run: (
    graphId: string,
    query: string,
    parameters: Record<string, unknown> = {},
    engine: CypherEngine = 'auto',
  ) =>
    api.post<CypherResult>(`${base(graphId)}/explorer/cypher`, {
      query,
      parameters,
      engine,
    }),
  queries: (graphId: string) => api.get<QueryLibrary>(`${base(graphId)}/explorer/queries`),
  save: (graphId: string, body: { name: string; query: string; description?: string }) =>
    api.post<{ query: SavedQuery }>(`${base(graphId)}/explorer/queries`, body).then((d) => d.query),
  remove: (graphId: string, queryId: string) =>
    api.delete<unknown>(`${base(graphId)}/explorer/queries/${queryId}`),
  rebuild: (graphId: string) =>
    api.post<{ nodes: number; edges: number; skipped: number }>(
      `${base(graphId)}/explorer/rebuild`,
      {},
    ),
}

export const pipelineApi = {
  status: (graphId: string) => api.get<PipelineStatus>(`${base(graphId)}/pipeline/status`),
  run: (graphId: string) => api.post<Record<string, unknown>>(`${base(graphId)}/pipeline/run`),
  progress: (graphId: string, runId: string) =>
    api.get<{ run_id: string; counts: Record<string, number> }>(
      `${base(graphId)}/pipeline/runs/${runId}/progress`,
    ),
  runs: (graphId: string) =>
    api.get<{ runs: PipelineRun[] }>(`${base(graphId)}/pipeline/runs`).then((d) => d.runs),
  retry: (graphId: string, messageId?: string) =>
    api.post<{ requeued: number }>(
      `${base(graphId)}/pipeline/retry${messageId ? `?message_id=${messageId}` : ''}`,
    ),
}

export const searchApi = {
  search: (graphId: string, q: string, params: Record<string, string | number | boolean> = {}) =>
    api.get<{ hits: SearchHit[] }>(`${base(graphId)}/search`, { q, ...params }).then((d) => d.hits),
}

export const generatorApi = {
  preview: (requirement: string, graphId?: string) =>
    api.post<DraftResult>('/brain/generate/preview', { requirement, graph_id: graphId }),
  generate: (body: { requirement: string; name?: string; proposal?: ModelProposal }) =>
    api.post<ApplyResult>('/brain/generate', body),
  generateInto: (graphId: string, body: { requirement: string; proposal?: ModelProposal }) =>
    api.post<ApplyResult>(`${base(graphId)}/generate`, body),
}
